import { apiMutate } from "~/utils/apiClient";

const { VITE_SEARCH_API } = import.meta.env;

export type SearchEntityType = "project" | "dataset" | "conversation";
export const SEARCH_ENTITY_TYPES: SearchEntityType[] = ["project", "dataset", "conversation"];

export interface SearchHit {
  entity_type: SearchEntityType;
  entity_id: string;
  title: string;
  /** Indexed text with matched terms in `<em>`. Null when only the vector leg matched. */
  snippet: string | null;
  score: number;
  /** 1-based rank within a leg, or null when that leg did not return the entity. */
  vector_rank: number | null;
  lexical_rank: number | null;
}

export interface SearchResponse {
  hits: SearchHit[];
  /** A floor: each leg stops at its limit. */
  type_counts: Partial<Record<SearchEntityType, number>>;
  /** Requested types the caller may see nothing of. */
  inaccessible_types: SearchEntityType[];
  vector_candidates: number;
  lexical_candidates: number;
  took_ms: number;
}

export interface SearchIndexTypeStatus {
  entity_type: SearchEntityType;
  entities: number;
  chunks: number;
  indexed_at: string | null;
  /** Rows on the model version search reads. */
  current_chunks: number;
}

/** How many chunks fall in one length bin. `upper` is null for the overflow bin. */
export interface SearchChunkSizeBucket {
  lower: number;
  upper: number | null;
  chunks: number;
  over_max: boolean;
}

export interface SearchIndexStatus {
  types: SearchIndexTypeStatus[];
  chunk_sizes: SearchChunkSizeBucket[];
}

export interface SearchIndexPendingTypeStatus {
  entity_type: SearchEntityType;
  collected: number;
  pending: number;
  new: number;
  text_changed: number;
  model_changed: number;
  blank: number;
  sample: { entity_id: string; title: string }[];
}

export interface SearchIndexPendingResponse {
  /** The newest-first window the counts cover. */
  window_limit: number;
  types: SearchIndexPendingTypeStatus[];
}

export interface SearchIndexTypeResult {
  entity_type: SearchEntityType;
  collected: number;
  embedded: number;
  chunks: number;
  unchanged: number;
  blank: number;
}

/** A recorded indexing run, filled in per type as it goes. */
export interface SearchIndexRun {
  id: string;
  started_at: string;
  completed_at: string | null;
  error: string | null;
  results: SearchIndexTypeResult[];
}

export interface SearchOrphanTypeStatus {
  entity_type: SearchEntityType;
  in_source: number;
  orphans: number;
  sample: string[];
  /** Why these orphans may not be deleted. */
  blocked: string | null;
}

export interface SearchOrphanResponse {
  types: SearchOrphanTypeStatus[];
}

/** Omitted when every type is selected, matching the backend default and its cache key. */
const appendEntityTypes = (params: URLSearchParams, entityTypes: SearchEntityType[]) => {
  if (entityTypes.length < SEARCH_ENTITY_TYPES.length) {
    entityTypes.forEach((entityType) => params.append("entity_types", entityType));
  }
};

/** Null when there is nothing to search for, which is SWR's signal not to fetch. */
export const searchPath = (
  query: string,
  entityTypes: SearchEntityType[],
  limit: number,
  updatedWithinDays: number | null
): string | null => {
  if (!query.trim()) return null;
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  appendEntityTypes(params, entityTypes);
  if (updatedWithinDays !== null) {
    params.set("updated_within_days", String(updatedWithinDays));
  }
  return `${VITE_SEARCH_API}?${params}`;
};

export const searchIndexPath = () => `${VITE_SEARCH_API}/index`;

/** Whole-corpus, since a newest-first window cannot show that an entity is gone. */
export const searchIndexOrphansPath = (entityTypes: SearchEntityType[]): string => {
  const params = new URLSearchParams();
  appendEntityTypes(params, entityTypes);
  const query = params.toString();
  return `${VITE_SEARCH_API}/index/orphans${query ? `?${query}` : ""}`;
};

/** Recomputes the orphans server-side rather than sending ids. */
export const runSearchPrune = (entityTypes: SearchEntityType[]) =>
  apiMutate(`${VITE_SEARCH_API}/index/prune`, "POST", { entity_types: entityTypes });

/** What an index run with the same types and window would do. */
export const searchIndexPendingPath = (
  entityTypes: SearchEntityType[],
  windowLimit: number
): string => {
  const params = new URLSearchParams({ window_limit: String(windowLimit) });
  appendEntityTypes(params, entityTypes);
  return `${VITE_SEARCH_API}/index/pending?${params}`;
};

/** Answers once the run is recorded; the indexing itself happens in the background. */
export const startSearchIndexRun = (entityTypes: SearchEntityType[], windowLimit: number) =>
  apiMutate<SearchIndexRun>(`${VITE_SEARCH_API}/index/runs`, "POST", {
    entity_types: entityTypes,
    window_limit: windowLimit,
  });

export const searchIndexRunPath = (runId: string) => `${VITE_SEARCH_API}/index/runs/${runId}`;

/** A run that died mid-way never sets either, so it reads as still running. */
export const isRunFinished = (run: SearchIndexRun) =>
  run.completed_at !== null || run.error !== null;
